var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useCallback, useMemo } from "react";
import { useNumberConversion } from "hooks/number-conversion";
import { useOpenFamilies } from "hooks/use-open-families";
import { usePendingPick } from "hooks/use-pending-pick";
import { Unit } from "utility/number";
import { getPumpChoiceColumns, getPumpFamilies, getPumpFamilyImages, getPumpNozzleSize, getRecommendedFeedPressureMpa, isPumpInFamilies, resolvePumpChoice, sortPumpsByNozzleSize, } from "utility/pump-families";
var familiesOf = function (pumps) {
    return Array.from(new Set(pumps.map(function (pump) { return pump.family; }).filter(Boolean)));
};
/*
 * The family → model two-step for a pump picker, shared by the central pump
 * (overlay + sidebar) and the decentral pump on a grip position. Which family
 * is open is picker-local UI state that follows the selected pump
 * (`useOpenFamilies`), the same way `useComponentSelect` treats cup families.
 *
 * `pumpOptions` is the full list the picker may offer. Both pickers — the
 * sidebar drawer and the in-scene overlay — pick within the open family
 * column by column (`pumpChoice`), and picking a family picks a pump straight
 * away (`onPickPumpFamily`). `pumpItemOptions` is the flat list they fall back
 * to while no option carries a family: grouped by nozzle & size, with the
 * recommended feed pressure as the row detail. `commitPump` puts a pump on
 * the nodes `nodeIds`.
 */
export var usePumpFamilySelection = function (pumpOptions, selectedPumpOptions, commitPump, nodeIds) {
    var convertNumber = useNumberConversion();
    var _a = __read(useOpenFamilies(familiesOf(selectedPumpOptions)), 2), selectedPumpFamilies = _a[0], setSelectedPumpFamilies = _a[1];
    var pumpFamilyOptions = useMemo(function () { return getPumpFamilies(pumpOptions); }, [pumpOptions]);
    var pumpFamilyImages = useMemo(function () { return getPumpFamilyImages(pumpOptions, pumpFamilyOptions); }, [pumpOptions, pumpFamilyOptions]);
    /* The unit the pressures are shown in; the header of the detail column. */
    var feedPressureUnit = convertNumber(0, Unit.MPA).unit;
    var familyPumps = useMemo(function () {
        return pumpOptions.filter(function (pump) {
            return isPumpInFamilies(pump, selectedPumpFamilies);
        });
    }, [pumpOptions, selectedPumpFamilies]);
    var formatFeedPressure = useCallback(function (pump) {
        var pressure = getRecommendedFeedPressureMpa(pump);
        return pressure === undefined
            ? undefined
            : String(convertNumber(pressure, Unit.MPA).value);
    }, [convertNumber]);
    var pumpItemOptions = useMemo(function () {
        return sortPumpsByNozzleSize(familyPumps).map(function (pump) { return ({
            value: pump.value,
            label: pump.label,
            image: pump.image,
            group: getPumpNozzleSize(pump),
            detail: formatFeedPressure(pump),
        }); });
    }, [familyPumps, formatFeedPressure]);
    var _b = usePendingPick(pumpOptions, selectedPumpOptions, commitPump, nodeIds), selectedPump = _b.picked, commit = _b.commit, isPicked = _b.isPicked;
    /* Picking a pump moves the open family to that pump's, so the picker shows
     * the pump among its siblings the next time it opens. */
    var onSelectPump = useCallback(function (value) {
        var pump = pumpOptions.find(function (option) { return option.value === value; });
        if (pump === null || pump === void 0 ? void 0 : pump.family)
            setSelectedPumpFamilies([pump.family]);
        commit(value);
    }, [pumpOptions, setSelectedPumpFamilies, commit]);
    /* Puts the pump of `pumps` that best fits `wanted` on the selected nodes,
     * unless they all have it already. */
    var pickPumpChoice = useCallback(function (pumps, wanted) {
        var pump = resolvePumpChoice(pumps, wanted);
        if (pump && !isPicked(pump.value))
            commit(pump.value);
    }, [isPicked, commit]);
    /* Opening another family picks its first pump right away — the top option
     * of each column — so the user only has to change the columns they care
     * about. Opening the pump's own family keeps the pump. */
    var onPickPumpFamily = useCallback(function (family) {
        setSelectedPumpFamilies([family]);
        if ((selectedPump === null || selectedPump === void 0 ? void 0 : selectedPump.family) === family)
            return;
        pickPumpChoice(pumpOptions.filter(function (pump) { return isPumpInFamilies(pump, [family]); }), {});
    }, [setSelectedPumpFamilies, pickPumpChoice, pumpOptions, selectedPump]);
    var pumpChoiceColumns = useMemo(function () { return getPumpChoiceColumns(familyPumps, selectedPump); }, [familyPumps, selectedPump]);
    var pumpChoice = useMemo(function () {
        var current = pumpChoiceColumns.selected;
        return __assign(__assign({}, pumpChoiceColumns), { selectedPump: selectedPump, selectedPumpDetail: selectedPump && formatFeedPressure(selectedPump), onSelectNozzleSize: function (nozzleSize) {
                return pickPumpChoice(familyPumps, __assign(__assign({}, current), { nozzleSize: nozzleSize }));
            }, onSelectEjectorCount: function (ejectorCount) {
                return pickPumpChoice(familyPumps, __assign(__assign({}, current), { ejectorCount: ejectorCount }));
            }, onSelectVariant: commit });
    }, [
        pumpChoiceColumns,
        selectedPump,
        formatFeedPressure,
        pickPumpChoice,
        familyPumps,
        commit,
    ]);
    return {
        pumpFamilyOptions: pumpFamilyOptions,
        pumpFamilyImages: pumpFamilyImages,
        selectedPumpFamilies: selectedPumpFamilies,
        onPickPumpFamily: onPickPumpFamily,
        pumpItemOptions: pumpItemOptions,
        pumpChoice: pumpChoice,
        feedPressureUnit: feedPressureUnit,
        onSelectPump: onSelectPump,
    };
};
